import React, { useEffect, useRef } from "react";
import {
  FaBirthdayCake,
  FaFlag,
  FaIdCard,
  FaMapMarkerAlt,
  FaTimes,
  FaUser,
  FaVenusMars,
  FaWeight,
} from "react-icons/fa";

import {
  DEFAULT_AVATAR,
  formatDateFr,
  getAdherentCreatedDate,
  getAdherentName,
  getAdherentPhoto,
  getAgeLabel,
  getGenreStyle,
} from "../utils/adminAdherentsUtils";

function InfoBox({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-700 shadow-sm dark:bg-slate-950 dark:text-sky-200">
            <Icon />
          </div>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>

          <p className="mt-2 break-words text-sm font-bold text-slate-800 dark:text-white/80">
            {value || "Non renseigné"}
          </p>
        </div>
      </div>
    </div>
  );
}

function AdherentDetailsDrawer({ isOpen, adherent, onClose }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !adherent) return null;

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-slate-950/70 backdrop-blur-sm">
      <aside
        className="h-full w-full max-w-2xl bg-white shadow-2xl dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-full flex-col">
          <div className="bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 p-5 text-white">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <img
                  src={getAdherentPhoto(adherent)}
                  alt={getAdherentName(adherent)}
                  className="h-16 w-16 rounded-[1.5rem] border border-white/20 object-cover"
                  onError={(event) => {
                    event.currentTarget.src = DEFAULT_AVATAR;
                  }}
                />

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                    Fiche adhérent
                  </p>

                  <h2 className="mt-2 font-titre text-2xl font-bold">
                    {getAdherentName(adherent)}
                  </h2>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span
                      className={`inline-flex min-h-8 items-center rounded-2xl px-3 text-xs font-bold ${getGenreStyle(
                        adherent.genre,
                      )}`}
                    >
                      {adherent.genre || "Non spécifié"}
                    </span>

                    <span className="inline-flex min-h-8 items-center rounded-2xl bg-white/15 px-3 text-xs font-bold text-white">
                      {getAgeLabel(adherent.date_naissance)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoBox icon={FaUser} label="ID adhérent" value={`#${adherent.id}`} />
              <InfoBox
                icon={FaBirthdayCake}
                label="Date de naissance"
                value={formatDateFr(adherent.date_naissance)}
              />
              <InfoBox icon={FaVenusMars} label="Genre" value={adherent.genre} />
              <InfoBox
                icon={FaWeight}
                label="Poids"
                value={adherent.poids ? `${adherent.poids} kg` : ""}
              />
              <InfoBox icon={FaMapMarkerAlt} label="Club" value={adherent.club} />
              <InfoBox
                icon={FaMapMarkerAlt}
                label="Département"
                value={adherent.departement}
              />
              <InfoBox icon={FaFlag} label="Pays" value={adherent.pays} />
              <InfoBox
                icon={FaIdCard}
                label="Numéro de licence"
                value={adherent.licence_number}
              />
              <InfoBox
                icon={FaIdCard}
                label="URL licence"
                value={adherent.licence_url}
              />
              <InfoBox
                icon={FaBirthdayCake}
                label="Date de création"
                value={formatDateFr(getAdherentCreatedDate(adherent))}
              />
            </div>

            {adherent.photo_url && (
              <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Photo enregistrée
                </p>

                <img
                  src={adherent.photo_url}
                  alt={getAdherentName(adherent)}
                  className="mt-4 max-h-80 w-full rounded-[1.5rem] object-contain"
                  onError={(event) => {
                    event.currentTarget.src = DEFAULT_AVATAR;
                  }}
                />
              </section>
            )}
          </div>

          <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-blue-800"
            >
              <FaTimes />
              Fermer la fiche
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default AdherentDetailsDrawer;